/* eslint-disable object-curly-newline */
/* global React */
import { Grid, useTheme, type GridSize } from '@mui/material'
import GaiaImageList from 'ui/Element/Attachment/List'
import fileConfig from 'configPlatform/ui/_shared/file.json5'
import MuiListTemplateSmall from 'ui/Element/Attachment/List/Template/Small'
import MuiListTemplateAction from 'ui/Element/Attachment/List/Template/Action'
import { type Attachment } from '@platform/react/hook/useAttachmentAPI'
import {
  type MessageClasses,
  type MessageEvents,
  type MessageVariant,
} from 'ui/Element/Message/List/Template/Message'

export interface BaseAttachmentsProps {
  /** a list of attachment objects */
  data?: Attachment[]
  /** message variant (primary / secondary) for alignment */
  variant?: MessageVariant
  /** the name of the message's attachment group */
  attachmentsGroup?: string
}

export interface AttachmentsProps extends BaseAttachmentsProps {
  /** an object containing the element's css classes */
  classes: MessageClasses
  /** whether the attachment is hidden */
  hidden?: boolean
  /** events for the component */
  events?: MessageEvents
  /** the grid width of each attachment */
  xs?: GridSize
}

/**
 * Attachments Component
 *
 * Splits the list of attachment objects passed as `data` into image and non-image files and
 * uses a different template to display each of them.
 *
 * @param props                  the component's properties
 * @param props.data             a list of attachment objects
 * @param props.classes          an object containing the element's css classes
 * @param props.hidden           whether to return null instead of the element
 * @param props.events           events for the component
 * @param props.attachmentsGroup attachment group
 * @param props.variant          message variant (drives image alignment)
 * @returns                      the attachments, or null
 */
const Attachments = ({
  data = [], classes, variant, hidden, events, attachmentsGroup, ...props
}: AttachmentsProps) => {
  const theme = useTheme() as any
  const placeholderProps = {
    size: '1.5rem',
    padding: '0.75rem',
    shape: 'rounded',
    ...variant === 'primary'
      ? {
        color: theme.palette.pending[700],
        background: theme.palette.pending[50],
      } : {
        color: theme.palette.black.main,
        background: theme.palette.gray[950],
      },
  }

  return hidden ? null : (
    <Grid
      {...props}
      item
      key={'attachments'}
      className={classes.attachments}
    >
      <GaiaImageList
        data={data}
        download={false}
        direction={variant === 'primary' ? 'row-reverse' : 'row'}

        types={fileConfig.types}
        filter={{ type: ['image'] }}
        ListItem={MuiListTemplateSmall}
        attachmentsGroup={attachmentsGroup}

        placeholderProps={placeholderProps}
        events={{ onClick: events?.onClick }}
        itemClasses={{ image: classes.attachedImage }}
      />
      <GaiaImageList
        data={data}
        download={false}
        direction={'column'}
        style={{ maxWidth: '18.75rem' }} // 300px

        types={fileConfig.types}
        filter={{ type: ['!image'] }}
        ListItem={MuiListTemplateAction}
        attachmentsGroup={attachmentsGroup}

        placeholderProps={placeholderProps}
        events={{ onClick: events?.onClick }}
        itemClasses={{ root: classes.genericAttachment }}
      />
    </Grid>
  )
}

export default Attachments
